import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Loader2, Users, FileText, Calendar, Archive, AlertTriangle, CheckCircle, UserCheck, Eye, RefreshCw } from 'lucide-react';
import { usePolicyDependencies } from '@/hooks/leave/usePolicyDependencies';
import type { PolicyDependencyReport } from '@/services/leave/policyDependencyService';

interface PolicyDeactivationDialogProps {
  policyId: string | null;
  policyName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  onAssignEmployees?: (policyId: string) => void;
  onViewBalances?: (policyId: string) => void;
}

export function PolicyDeactivationDialog({
  policyId,
  policyName,
  open,
  onOpenChange,
  onConfirm,
  onAssignEmployees,
  onViewBalances
}: PolicyDeactivationDialogProps) {
  const { 
    data: dependencyReport, 
    isLoading: loading, 
    error, 
    refetch: refetchDependencies 
  } = usePolicyDependencies(policyId || '', open && !!policyId);

  const handleDeactivate = () => {
    onConfirm();
    onOpenChange(false);
  };

  const handleManageEmployees = () => {
    if (policyId && onAssignEmployees) {
      onAssignEmployees(policyId);
      onOpenChange(false); // Close dialog since user is going to manage assignments
    }
  };

  const handleViewBalances = () => {
    if (policyId && onViewBalances) {
      onViewBalances(policyId);
    }
  };

  const renderDependencySection = (
    title: string,
    count: number,
    details: any[],
    icon: React.ComponentType<any>,
    isBlocking: boolean = false
  ) => {
    if (count === 0) return null;

    const IconComponent = icon;
    
    return (
      <Card className={`${isBlocking ? 'border-destructive/50 bg-destructive/5' : 'border-muted'}`}>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <IconComponent className="h-4 w-4" />
            {title}
            <Badge variant={isBlocking ? 'destructive' : 'secondary'}>
              {count}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-2 max-h-24 overflow-y-auto">
            {details.slice(0, 3).map((detail, index) => (
              <div key={index} className="text-xs text-muted-foreground">
                • {detail.employee_name || 'Unknown'}
                {detail.start_date && ` (${detail.start_date})`}
                {detail.status && ` - ${detail.status}`}
              </div>
            ))}
            {details.length > 3 && (
              <div className="text-xs text-muted-foreground font-medium">
                ...and {details.length - 3} more
              </div>
            )}
          </div>
          
          {/* Quick Actions for Dependencies */}
          {isBlocking && count > 0 && policyId && (
            <div className="flex gap-2 mt-3 pt-3 border-t">
              {title.includes('Employee') && onAssignEmployees && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleManageEmployees}
                  className="text-xs h-7"
                >
                  <UserCheck className="h-3 w-3 mr-1" />
                  Manage Employees
                </Button>
              )}
              {(title.includes('Leave') || title.includes('Balance')) && onViewBalances && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleViewBalances}
                  className="text-xs h-7"
                >
                  <Eye className="h-3 w-3 mr-1" />
                  View {title.includes('Request') ? 'Requests' : 'Balances'}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderLoadingState = () => (
    <div className="flex flex-col items-center justify-center py-8 space-y-3">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      <div className="text-center">
        <p className="text-sm font-medium">Checking policy dependencies...</p>
        <p className="text-xs text-muted-foreground">
          Analyzing employee assignments, leave requests, and balances
        </p>
      </div>
    </div>
  );

  const renderErrorState = () => (
    <div className="flex flex-col items-center justify-center py-8 space-y-3">
      <AlertTriangle className="h-8 w-8 text-destructive" />
      <div className="text-center">
        <p className="text-sm font-medium text-destructive">Failed to load dependency information</p>
        <p className="text-xs text-muted-foreground">{error?.message || 'Unknown error'}</p>
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => refetchDependencies()}
        >
          Try Again
        </Button>
      </div>
    </div>
  );

  const pendingApprovedRequests = dependencyReport?.dependencies.leaveRequests.details.filter(
    req => req.status === 'pending' || req.status === 'approved'
  ) || [];

  const canDeactivate = dependencyReport?.canDelete || false;
  const hasBlockingDependencies = (dependencyReport?.blockingDependencies || 0) > 0;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5" />
            Deactivate Policy: {policyName}
          </AlertDialogTitle>
          <AlertDialogDescription>
            Deactivating a policy will prevent it from being assigned to new employees, but preserves all existing data.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4">
          {loading && renderLoadingState()}
          
          {error && renderErrorState()}
          
          {dependencyReport && !loading && !error && (
            <>
              {/* Refresh Analysis Button */}
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetchDependencies()}
                  className="text-xs"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Refresh Analysis
                </Button>
              </div>

              {/* Status Banner */}
              <div className={`p-4 rounded-lg border ${
                canDeactivate 
                  ? 'bg-green-50 border-green-200 text-green-800'
                  : 'bg-yellow-50 border-yellow-200 text-yellow-800'
              }`}>
                <div className="flex items-center gap-2">
                  {canDeactivate ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  )}
                  <div>
                    <p className="font-medium">
                      {canDeactivate ? 'Policy can be safely deactivated' : 'Policy has blocking dependencies'}
                    </p>
                    <p className="text-sm">
                      {canDeactivate 
                        ? 'No blocking dependencies found. This policy can be deactivated without issues.'
                        : 'Please resolve the following dependencies before deactivating this policy.'
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Dependencies Overview */}
              {dependencyReport.totalDependencies > 0 && (
                <>
                  <Separator />
                  <div>
                    <h4 className="text-sm font-medium mb-3">Impact Analysis</h4>
                    <div className="grid grid-cols-1 gap-3">
                      {renderDependencySection(
                        'Active Employee Assignments',
                        dependencyReport.dependencies.activeAssignments.count,
                        dependencyReport.dependencies.activeAssignments.details,
                        Users,
                        true
                      )}
                      
                      {renderDependencySection(
                        'Pending/Approved Leave Requests',
                        pendingApprovedRequests.length,
                        pendingApprovedRequests,
                        Calendar,
                        true
                      )}
                      
                      {renderDependencySection(
                        'Leave Balances',
                        dependencyReport.dependencies.leaveBalances.count,
                        dependencyReport.dependencies.leaveBalances.details,
                        FileText,
                        false
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* What happens when deactivated */}
              <Separator />
              <div>
                <h4 className="text-sm font-medium mb-2">What happens when a policy is deactivated?</h4>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Policy will no longer appear in assignment options for new employees</li>
                  <li>• Existing employee assignments will remain intact</li>
                  <li>• Leave balances and history are preserved</li>
                  <li>• Policy can be reactivated at any time</li>
                  <li>• No data is permanently deleted</li>
                </ul>
              </div>
            </>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDeactivate}
            disabled={loading || !!error || hasBlockingDependencies}
            className={hasBlockingDependencies ? 'bg-destructive hover:bg-destructive/90' : ''}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Checking...
              </>
            ) : hasBlockingDependencies ? (
              'Cannot Deactivate'
            ) : (
              'Deactivate Policy'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}